import cloneDeep from 'lodash/cloneDeep';
import apiService from 'src/utils/apiService';
import { getNecessaryLimit } from 'src/utils/helpers';
import { maxCountLoadingMoreBtn as onePageSize } from 'src/utils/config';
import * as types from '../mutation-types';

// initial state
const state = {
  tickersList: [],
  userFavoriteTickersList: [],
  tickersUpdateDate: 0,
  totalRowsCount: 0,
  foundRowsCount: 0,
  totalRowsCountFavortiteTickers: 0,
  foundRowsCountFavortiteTickers: 0,
  numberNewCurrencies: 0,
  totalMarketCapPercentChange: null,
  totalMarketCap: null,
  totalMarketVolumePercentChange: null,
  totalVolume24h: null,
  mostChangedTickersList: [],
  recentlyAddedList: [],
  leaderTicker: null,
};

function generatePlaceholderMessage(count) {
  if (!count) {
    return '';
  }
  if (count > 1) {
    return `Search among ${count} currencies`;
  }
  return 'There is only 1 currency';
}

// getters
const getters = {
  searchControlPlaceholder(s) {
    return generatePlaceholderMessage(s.totalRowsCount);
  },
  searchControlFavouritesPlaceholder(s) {
    return generatePlaceholderMessage(s.totalRowsCountFavortiteTickers);
  },
};

// actions
const actions = {
  updateTickers({ commit, state }, params) { // eslint-disable-line
    const changedParams = {
      ...params,
    };
    if (!changedParams.limit) {
      changedParams.limit = getNecessaryLimit(state.tickersList.length, onePageSize);
    }
    return apiService.getTickers(changedParams).then((data) => {
      commit(types.UPDATE_TICKERS, data.rows);
      commit(types.UPDATE_TOTAL_ROWS_COUNT, data.totalRowsCount);
      commit(types.UPDATE_FOUND_ROWS_COUNT, data.foundRowsCount);
      commit(types.UPDATE_TOTAL_MARKET_CAP_PERCENT_24H, {
        usd: data.totalMarketCapPercentChangeUSD,
        eur: data.totalMarketCapPercentChangeEUR,
      });
      commit(types.UPDATE_TOTAL_VOLUME_PERCENT_24H, {
        usd: data.totalMarketVolumePercentChangeUSD,
        eur: data.totalMarketVolumePercentChangeEUR,
      });
      commit(types.UPDATE_TOTAL_MARKET_CAP, {
        usd: data.totalMarketCapUSD,
        eur: data.totalMarketCapEUR,
      });
      commit(types.UPDATE_TOTAL_VOLUME_24H, {
        usd: data.totalVolume24hUSD,
        eur: data.totalVolume24hEUR,
      });
    });
  },
  loadMoreTickers({ commit, state }, params) { // eslint-disable-line
    const changedParams = {
      ...params,
      limit: onePageSize,
      offset: getNecessaryLimit(state.tickersList.length, onePageSize),
    };
    return apiService.getTickers(changedParams).then((data) => {
      commit(types.ADD_MORE_TICKERS, data.rows);
      commit(types.UPDATE_TOTAL_ROWS_COUNT, data.totalRowsCount);
      commit(types.UPDATE_FOUND_ROWS_COUNT, data.foundRowsCount);
    });
  },
  updateUserFavoriteTickers({ commit, state }, params) { // eslint-disable-line
    const changedParams = {
      ...params,
      limit: getNecessaryLimit(state.userFavoriteTickersList.length, onePageSize),
    };
    return apiService.getUserFavoriteTickers(changedParams).then((data) => {
      commit(types.UPDATE_USER_FAVORITE_TICKERS, data.rows);
      commit(types.UPDATE_TOTAL_ROWS_COUNT_USER_FAVORITE, data.totalRowsCount);
      commit(types.UPDATE_FOUND_ROWS_COUNT_USER_FAVORITE, data.foundRowsCount);
    });
  },
  loadMoreUserFavoriteTickers({ commit, state }, params) { // eslint-disable-line
    const changedParams = {
      ...params,
      limit: onePageSize,
      offset: getNecessaryLimit(state.userFavoriteTickersList.length, onePageSize),
    };
    return apiService.getUserFavoriteTickers(changedParams).then((data) => {
      commit(types.ADD_MORE_FAVORITE_TICKERS, data.rows);
      commit(types.UPDATE_TOTAL_ROWS_COUNT_USER_FAVORITE, data.totalRowsCount);
      commit(types.UPDATE_FOUND_ROWS_COUNT_USER_FAVORITE, data.foundRowsCount);
    });
  },
  addToFavoriteUserTickers({ commit, state }, currency) { // eslint-disable-line
    return apiService.addTickerToFavorite(currency.id).then(() => {
      commit(types.ADD_TO_FAVORITE_TICKERS, currency);
    });
  },
  removeFromFavoriteUserTickers({ commit, state }, currency) { // eslint-disable-line
    return apiService.removeTickerFromFavorite(currency.id).then(() => {
      commit(types.REMOVE_FROM_FAVORITE_TICKERS, currency);
    });
  },
  updateNumberNewCurrencies({ commit, state }) { // eslint-disable-line
    return apiService.getNumberNewCurrencies().then((result) => {
      commit(types.UPDATE_NUMBER_NEW_CURRENCIES, result);
    });
  },
  getMostChangedTickersList({ commit, state }) { // eslint-disable-line
    return apiService.getMostChangedTickers().then((data) => {
      commit(types.SET_MOST_CHANGED_TICKERS, data);
    });
  },
  getRecentlyAddedList({ commit, state }) { // eslint-disable-line
    return apiService.getRecentlyAdded().then((data) => {
      commit(types.SET_RECENTLY_ADDED, data);
    });
  },
  getLeaderTicker({ commit, state }) {// eslint-disable-line
    return apiService.getTickers({ limit: 1 }).then((data) => {
      commit(types.SET_LEADER_TICKER, data.rows[0]);
    });
  },
};

// mutations
const mutations = {
  [types.UPDATE_TICKERS](s, tickers) {
    s.tickersList = cloneDeep(tickers); // eslint-disable-line
    s.tickersUpdateDate = Date.now(); // eslint-disable-line
  },
  [types.ADD_MORE_TICKERS](s, tickers) {
    s.tickersList = [...s.tickersList, ...cloneDeep(tickers)]; // eslint-disable-line
  },
  [types.UPDATE_TOTAL_MARKET_CAP_PERCENT_24H](s, totalMarketCapPercentChange) {
    s.totalMarketCapPercentChange = totalMarketCapPercentChange; // eslint-disable-line
  },
  [types.UPDATE_TOTAL_MARKET_CAP](s, totalMarketCap) {
    s.totalMarketCap = totalMarketCap; // eslint-disable-line
  },
  [types.UPDATE_TOTAL_VOLUME_PERCENT_24H](s, totalMarketVolumePercentChange) {
    s.totalMarketVolumePercentChange = totalMarketVolumePercentChange; // eslint-disable-line
  },
  [types.UPDATE_TOTAL_VOLUME_24H](s, totalVolume24h) {
    s.totalVolume24h = totalVolume24h; // eslint-disable-line
  },
  [types.UPDATE_TOTAL_ROWS_COUNT](s, totalRowsCount) {
    s.totalRowsCount = totalRowsCount; // eslint-disable-line
  },
  [types.UPDATE_FOUND_ROWS_COUNT](s, foundRowsCount) {
    s.foundRowsCount = foundRowsCount; // eslint-disable-line
  },
  [types.UPDATE_USER_FAVORITE_TICKERS](s, userFavoriteTickers) {
    s.userFavoriteTickersList = cloneDeep(userFavoriteTickers); // eslint-disable-line
  },
  [types.ADD_MORE_FAVORITE_TICKERS](s, userFavoriteTickers) {
    s.userFavoriteTickersList = [...s.userFavoriteTickersList, ...cloneDeep(userFavoriteTickers)]; // eslint-disable-line
  },
  [types.UPDATE_TOTAL_ROWS_COUNT_USER_FAVORITE](s, totalRowsCount) {
    s.totalRowsCountFavortiteTickers = totalRowsCount; // eslint-disable-line
  },
  [types.UPDATE_FOUND_ROWS_COUNT_USER_FAVORITE](s, foundRowsCount) {
    s.foundRowsCountFavortiteTickers = foundRowsCount; // eslint-disable-line
  },
  [types.ADD_TO_FAVORITE_TICKERS](s, currency) {
    const foundCur = s.tickersList.find((t => t.id === currency.id));
    foundCur.isFavourite = true; // TODO still we need that ?
  },
  [types.REMOVE_FROM_FAVORITE_TICKERS](s, currency) {
    const foundCur = s.tickersList.find((t => t.id === currency.id));
    foundCur.isFavourite = false; // TODO still we need that ?
  },
  [types.UPDATE_NUMBER_NEW_CURRENCIES](s, result) {
    s.numberNewCurrencies = result; // eslint-disable-line
  },
  [types.SET_MOST_CHANGED_TICKERS](s, currencies) {
    s.mostChangedTickersList = cloneDeep(currencies); // eslint-disable-line
  },
  [types.SET_RECENTLY_ADDED](s, currencies) {
    s.recentlyAddedList = cloneDeep(currencies); // eslint-disable-line
  },
  [types.SET_LEADER_TICKER](s, leaderTickerData) {
    s.leaderTicker = leaderTickerData; // eslint-disable-line
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/tickers.js